import { Container, Title, Text, Box, List, Anchor } from "@mantine/core";

export default function PrivacyPage() {
  return (
    <Box py={60} bg="white" style={{ minHeight: "60vh" }}>
      <Container size="md">
        <Title order={1} mb="xl" c="primary.6">
          Privacy Policy
        </Title>

        <Box c="slate.8" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Text lh={1.6}>
            This Privacy Policy (<strong>"Privacy Policy"</strong>) explains how <em>GT Setu Connections Pv Ltd</em> (
            <strong>"Company"</strong>, <strong>"we"</strong>, <strong>"our"</strong>, or <strong>"us"</strong>)
            collects, uses, stores, discloses, and protects information when companies, their authorised
            representatives, and visitors (collectively, <strong>"User/s"</strong>, <strong>"you"</strong>, or{" "}
            <strong>"your"</strong>) access or use our website, platform, applications, tools, including any content,
            functionality, and related services (collectively, the <strong>"Platform"</strong>).
          </Text>

          <Text lh={1.6}>
            The Company operates a business-to-business (B2B) matchmaking and collaboration platform that enables
            companies across various jurisdictions to discover, evaluate, and connect with other companies for potential
            domestic and cross-border collaborations. We are committed to respecting your privacy and safeguarding the
            information entrusted to us.
          </Text>

          <Text lh={1.6}>
            This Privacy Policy applies globally and is intended to comply with applicable data protection and privacy
            laws, including but not limited to the laws of India and other jurisdictions where our Users are located.
            Where required, we implement additional safeguards to ensure that personal and business-related information
            is processed lawfully, fairly, and transparently.
          </Text>

          <Text lh={1.6}>
            Please read this Privacy Policy carefully to understand the Company's policies and practices regarding your
            information and how the Company will treat it. By accessing or using the Platform, you acknowledge that you
            have read and understood this Privacy Policy and agree to the collection and processing of information in
            accordance with its terms. If you do not agree with this Privacy Policy, you should refrain from accessing
            or using the Platform. This Privacy Policy may change from time to time. Your continued use of this Platform
            after the Company make changes is deemed to be acceptance of those changes, so please check the Privacy
            Policy periodically for updates.
          </Text>

          <Title order={3} mt="sm">
            SCOPE AND APPLICABILITY
          </Title>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                If you access, browse, register on, or otherwise use the Platform whether as a visitor, a registered
                company, or an authorised representative of a company (collectively, “Users”) the Company may collect
                and process certain information relating to you in connection with such use.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                For the purposes of this Privacy Policy, “Personal Data” means any information relating to an identified
                or identifiable natural person. “Processing” in relation to Personal Data means any operation or set of
                operations performed on Personal Data, whether wholly or partly automated, including collection,
                recording, organisation, structuring, storage, adaptation or alteration, retrieval, use, sharing,
                disclosure, restriction, erasure, or destruction.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                This Privacy Policy applies only to information that the Company collects and processes through
                electronic or digital means, based on consent or any other lawful basis, in connection with the
                Company's website, web-based platform, applications, email, text, and other electronic messages between
                you and this Platform, at the time of you signing-up to create your account on the Platform, including
                but not limited to any information collected by for the provision of the Services (as such term has been
                defined in the Company's Terms and Conditions.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                This Privacy Policy does not apply to information collected by third-party websites, platforms,
                services, or applications that may be linked to or accessible from the Platform. The Company is not
                responsible for the privacy practices or content of such third parties, and Users are encouraged to
                review their privacy policies before providing any information.
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                This Privacy Policy applies to Users globally, subject to applicable mandatory data protection and
                privacy laws in their respective jurisdictions.
              </Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            INFORMATION WE COLLECT & PROCESS
          </Title>

          <Text lh={1.6}>
            In connection with the operation of the Platform and the services offered, the Company may collect and
            process the following categories of information:
          </Text>

          <List type="ordered" spacing="md" withPadding>
            <List.Item>
              <Text lh={1.6} fw={600}>
                Information Provided by Companies and Authorised Representatives
              </Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>Company name, legal structure, registration details, and registered address;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Business identifiers and verification details, including Udyam Registration, Corporate
                    Identification Number (CIN), Goods and Services Tax (GST) details, and other similar identifiers;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>Industry classification, business description, and operational details;</Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Contact details of authorised representatives, including names, phone numbers, and email addresses;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Details of products and services offered by the company, including descriptions and images;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Company profiles, images, brochures, websites, and other business-related information voluntarily
                    submitted on the Platform.
                  </Text>
                </List.Item>
              </List>
            </List.Item>

            <List.Item>
              <Text lh={1.6} fw={600}>
                Verification and Compliance Information
              </Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>
                    Information required for verifying the authenticity and status of a company, including incorporation
                    details, director details, authorised and paid-up capital, statutory filings, and other publicly
                    available or lawfully obtained corporate records;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Verification outcomes and platform-generated identifiers, including reference numbers and
                    verification status indicators.
                  </Text>
                </List.Item>
              </List>
            </List.Item>

            <List.Item>
              <Text lh={1.6} fw={600}>
                Interaction, Communication, and Collaboration Data
              </Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>
                    Information relating to expressions of interest, acceptances, and connections between companies;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Communications exchanged through the Platform, including messages, notices, and system-generated
                    communications;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Documents and information shared between companies on the Platform, including technical, commercial,
                    financial, and legal documents, subject to access controls and permissions set by the Users;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Records, logs, and timestamps relating to document exchange, communications, and collaboration
                    activities.
                  </Text>
                </List.Item>
              </List>
            </List.Item>

            <List.Item>
              <Text lh={1.6} fw={600}>
                Transactional and Payment Information
              </Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>
                    Information necessary to process payments for paid services, connections, or closure reports,
                    including transaction identifiers and payment status (excluding full payment instrument details
                    where processed by third-party payment service providers).
                  </Text>
                </List.Item>
              </List>
            </List.Item>

            <List.Item>
              <Text lh={1.6} fw={600}>
                Technical and Usage Information
              </Text>
              <List type="ordered" spacing="xs" withPadding mt="xs" style={{ listStyleType: "lower-alpha" }}>
                <List.Item>
                  <Text lh={1.6}>
                    Information automatically collected when Users access or use the Platform, such as IP address,
                    device and browser information, log files, access times, and usage patterns;
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    Platform activity data for security, audit, analytics, and service improvement purposes.
                  </Text>
                </List.Item>
                <List.Item>
                  <Text lh={1.6}>
                    The Company processes such information only to the extent necessary for providing the Platform,
                    enabling company discovery and collaboration, ensuring verification and security, complying with
                    legal obligations, and improving its services.
                  </Text>
                </List.Item>
              </List>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            PURPOSE OF PROCESSING
          </Title>

          <Text lh={1.6}>
            The Company processes the information collected through the Platform for the following purposes:
          </Text>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>to present this Platform and its contents to you;</Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                to enable company discovery, profiling, and matchmaking based on business interests, sectors, and
                geography;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                to provide you with information, products, or services that you request from the Company;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                to verify company details and establish the authenticity and status of registered companies;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                to facilitate expressions of interest, connections, and communications between companies on the
                Platform;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                to provide secure infrastructure for document sharing, collaboration, and controlled information
                exchange;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>to process payments for paid services, connections, and closure-related reports;</Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                to maintain records, logs, and audit trails relating to Platform activity, communications, and document
                exchanges;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                to ensure Platform security, prevent misuse, fraud, or unauthorised access, and enforce the Company's
                terms and policies;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>to comply with applicable legal, regulatory, and contractual obligations; and</Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                to improve, maintain, and develop the Platform, including analytics, troubleshooting, and service
                enhancement.
              </Text>
            </List.Item>
          </List>

          <Text lh={1.6}>
            The Company processes information only to the extent necessary for the above purposes and in accordance with
            applicable data protection and privacy laws.
          </Text>

          <Text lh={1.6}>
            We may use automated tools, including artificial intelligence (“AI”) and machine learning technologies, to
            process and analyse information provided by Users for the limited purpose of improving the functionality,
            efficiency, and performance of the Platform. Such use may include, without limitation:
          </Text>

          <List type="ordered" spacing="sm" withPadding style={{ listStyleType: "lower-alpha" }}>
            <List.Item>
              <Text lh={1.6}>Enhancing search relevance and filtering;</Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>Improving categorisation and tagging of company profiles;</Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>Standardising or organising service descriptions;</Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>Detecting duplicate, incomplete, or inconsistent information; and</Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>Improving overall discoverability and User experience.</Text>
            </List.Item>
          </List>

          <Title order={3} mt="xl">
            LEGAL BASIS FOR PROCESSING
          </Title>

          <Text lh={1.6}>
            The Company processes information in accordance with applicable data protection and privacy laws across
            jurisdictions and relies on one or more lawful bases, as permitted under such laws, depending on the nature
            of the information and the purpose for which it is processed.
          </Text>

          <Text lh={1.6}>The lawful bases relied upon by the Company include the following:</Text>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                <strong>Consent:</strong> Where Users voluntarily provide information through the Platform or expressly
                consent to the processing of information for specific purposes, including registration, profile
                creation, verification, communications, document exchange, and receipt of platform-related
                notifications. Where consent is the lawful basis, Users may withdraw such consent in accordance with
                this Privacy Policy, subject to legal and contractual limitations.
              </Text>
            </List.Item>

            <List.Item>
              <Text lh={1.6}>
                <strong>Performance of Contract:</strong> Where processing is necessary for the performance of a
                contract or to take steps at the request of the User prior to entering into a contract. This includes
                enabling access to the Platform, creating and managing company profiles, verifying company information,
                facilitating expressions of interest, enabling connections between companies, providing paid services,
                and generating reports or records associated with Platform usage.
              </Text>
            </List.Item>

            <List.Item>
              <Text lh={1.6}>
                <strong>Legitimate Interests:</strong> Where processing is necessary for the legitimate business
                interests of the Company, including maintaining and operating the Platform, ensuring Platform security
                and integrity, preventing fraud, misuse, or unauthorised access, maintaining audit logs and records,
                improving Platform functionality, conducting analytics, and enforcing the Company's terms, policies, and
                agreements. Such processing is undertaken only where these interests are not overridden by the rights
                and freedoms of Users.
              </Text>
            </List.Item>

            <List.Item>
              <Text lh={1.6}>
                <strong>Compliance with Legal Obligations:</strong> Where processing is necessary to comply with
                applicable laws, regulations, regulatory requirements, court orders, governmental requests, or other
                legal processes, including record-keeping, disclosures to authorities, and compliance-related audits.
              </Text>
            </List.Item>

            <List.Item>
              <Text lh={1.6}>
                <strong>Protection of Rights and Interests:</strong> Where processing is necessary to establish,
                exercise, or defend legal claims, resolve disputes, investigate potential violations, or protect the
                rights, property, or safety of the Company, its Users, or third parties.
              </Text>
            </List.Item>
          </List>

          <Text lh={1.6}>
            The Company ensures that information is processed only to the extent necessary for the applicable lawful
            basis and implements appropriate safeguards in accordance with applicable data protection and privacy laws.
          </Text>

          <Title order={3} mt="xl">
            DISCLOSURE AND SHARING OF INFORMATION
          </Title>

          <Text lh={1.6}>
            The Company may disclose or share information collected through the Platform strictly in accordance with
            this Privacy Policy, Terms and Conditions and only to the extent necessary for the operation of the Platform
            and the provision of its Services. Information may be disclosed or shared in the following circumstances:
          </Text>

          <List type="ordered" spacing="sm" withPadding>
            <List.Item>
              <Text lh={1.6}>
                <strong>Between Companies on the Platform:</strong> Where two or more companies express mutual interest
                and complete the applicable requirements, the Company may disclose relevant company and contact details
                to the concerned parties to enable collaboration. Certain information and documents may be disclosed
                only after completion of payment obligations and, where applicable, execution of a non-disclosure
                agreement (NDA) through the Platform.
              </Text>
            </List.Item>

            <List.Item>
              <Text lh={1.6}>
                <strong>Document Exchange and Controlled Access:</strong> Information and documents shared by Users
                through the Platform for technical, commercial, financial, or legal evaluation are disclosed only to the
                intended recipient companies, subject to access controls, permissions, and restrictions selected by the
                Users. The Company maintains records and logs of such exchanges for operational and audit purposes.
              </Text>
            </List.Item>

            <List.Item>
              <Text lh={1.6}>
                <strong>Service Providers and Partners:</strong> The Company may share information with third-party
                service providers, consultants, or partners who support the operation of the Platform, including
                verification services, payment processing, cloud hosting, data storage, analytics, communication tools,
                and customer support. Such parties are engaged under contractual obligations to use the information
                solely for the specified purposes and to maintain appropriate confidentiality and security standards.
              </Text>
            </List.Item>

            <List.Item>
              <Text lh={1.6}>
                <strong>Legal and Regulatory Disclosures:</strong> The Company may disclose information where required
                to comply with applicable laws, regulations, court orders, governmental or regulatory requests, or to
                respond to lawful inquiries by competent authorities.
              </Text>
            </List.Item>

            <List.Item>
              <Text lh={1.6}>
                <strong>Protection of Rights and Interests:</strong> Information may be disclosed where necessary to
                investigate, prevent, or address suspected or actual violations, fraud, security issues, or to protect
                the rights, property, or safety of the Company, Users, or third parties.
              </Text>
            </List.Item>
          </List>

          <Text lh={1.6}>
            The Company does not sell personal information to third parties. Any disclosure or sharing of information is
            limited to what is reasonably necessary for the relevant purpose and is carried out in accordance with
            applicable data protection and privacy laws.
          </Text>

          <Title order={3} mt="xl">
            CROSS-BORDER DATA TRANSFERS
          </Title>

          <Text lh={1.6}>
            Given the global nature of the Platform, information collected and processed by the Company may be
            transferred to, stored in, or accessed from jurisdictions outside the country in which a User is located.
            Such cross-border transfers may occur to enable company discovery, verification, collaboration, document
            exchange, Platform hosting, analytics, and support services.
          </Text>

          <Text lh={1.6}>
            Where information is transferred across borders, the Company takes reasonable steps to ensure that such
            transfers are carried out in accordance with applicable data protection and privacy laws and that
            appropriate safeguards are implemented to protect the information. These safeguards may include contractual
            arrangements, data protection agreements, and other lawful mechanisms recognised under applicable laws.
          </Text>

          <Text lh={1.6}>
            By accessing or using the Platform and providing information, Users acknowledge and consent to the transfer
            of information across borders as necessary for the purposes set out in this Privacy Policy, subject to
            applicable legal requirements.
          </Text>

          <Title order={3} mt="xl">
            DATA RETENTION
          </Title>

          <Text lh={1.6}>
            The Company retains information collected and processed through the Platform only for as long as necessary
            to fulfil the purposes for which it was collected, as outlined in this Privacy Policy, unless a longer
            retention period is required or permitted under applicable laws.
          </Text>

          <Text lh={1.6}>In general, the Company retains the following information:</Text>

          <List type="ordered" spacing="sm" withPadding style={{ listStyleType: "lower-alpha" }}>
            <List.Item>
              <Text lh={1.6}>
                Information relating to registered companies, profiles, verification status, and Platform activity is
                retained for the duration of the company's use of the Platform;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Information and documents exchanged between companies in connection with expressions of interest,
                collaborations, and evaluations are retained for the duration of the active collaboration period,
                including the standard collaboration window made available on the Platform;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Logs, audit trails, records of communications, document exchanges, and transaction-related records may
                be retained for a reasonable period thereafter for compliance, dispute resolution, security, and audit
                purposes;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                Final reports or closure-related records generated at the conclusion of a collaboration may be retained
                in accordance with contractual obligations and applicable legal requirements.
              </Text>
            </List.Item>
          </List>

          <Text lh={1.6}>
            Upon account closure, withdrawal of consent, or termination of services, the Company will retain, such
            personal data, associated traffic data and other logs of the processing for a minimum period of one (1) year
            from the date of such processing and will delete or anonymise information in its possession thereafter,
            unless retention is required to comply with legal obligations, resolve disputes, enforce agreements, or
            protect the rights and interests of the Company or its Users.
          </Text>

          <Title order={3} mt="xl">
            USER RIGHTS
          </Title>

          <Text lh={1.6}>
            Subject to applicable data protection and privacy laws, Users have certain rights in relation to the
            information processed by the Company through the Platform. These rights may vary depending on the User's
            jurisdiction and the nature of the information involved.
          </Text>

          <Text lh={1.6}>Users may have the right to:</Text>

          <List type="ordered" spacing="sm" withPadding style={{ listStyleType: "lower-alpha" }}>
            <List.Item>
              <Text lh={1.6}>request access to information processed about them;</Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>request correction or updating of inaccurate or incomplete information;</Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                request deletion or erasure of information, subject to legal, contractual, and operational limitations;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                object to or request restriction of certain processing activities, where permitted by applicable law;
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                withdraw consent where processing is based on consent, without affecting the lawfulness of processing
                carried out prior to such withdrawal.
              </Text>
            </List.Item>
          </List>

          <Text lh={1.6}>
            The Company may require reasonable verification of a User's identity and authority before responding to a
            request. Requests will be addressed within the timeframes prescribed under applicable laws.
          </Text>

          <Text lh={1.6}>
            Certain rights may be limited where processing is necessary to comply with legal obligations, perform
            contractual obligations, maintain security and audit records, or protect the rights and interests of the
            Company or other Users.
          </Text>

          <Title order={3} mt="xl">
            WITHDRAWAL OF CONSENT AND ACCOUNT CLOSURE
          </Title>

          <Text lh={1.6}>
            Where the processing of information is based on consent, Users may withdraw such consent at any time by
            following the mechanisms made available on the Platform or by contacting the Company in accordance with this
            Privacy Policy. Withdrawal of consent shall not affect the lawfulness of processing carried out prior to
            such withdrawal.
          </Text>

          <Text lh={1.6}>
            Users may request closure of their account or discontinuation of services through the Platform or by
            submitting a request to the Company. Upon account closure, access to the Platform and its features may be
            restricted or terminated.
          </Text>

          <Text lh={1.6}>
            Withdrawal of consent or account closure may result in the inability of the Company to provide certain
            services, including access to company discovery, matchmaking, communications, document exchange, and
            collaboration features.
          </Text>

          <Text lh={1.6}>
            Notwithstanding withdrawal of consent or account closure, the Company may retain certain information where
            such retention is necessary to comply with legal or regulatory obligations, enforce contractual rights,
            resolve disputes, maintain security and audit records, or protect the rights and interests of the Company or
            other Users, in accordance with this Privacy Policy.
          </Text>

          <Title order={3} mt="xl">
            MINORS
          </Title>

          <Text lh={1.6}>
            The Platform is intended solely for use by companies and their authorised representatives acting in a
            professional or commercial capacity. Our Platform is not directed to, and we do not intend to, nor do we
            knowingly collect or solicit, personal information online from individuals under the age of 18 (“Minors”).
          </Text>

          <Text lh={1.6}>
            By accessing or using the Platform, Users represent and warrant that they are at least 18 years of age and
            have the legal capacity and authority to use the Platform on behalf of the relevant company.
          </Text>

          <Text lh={1.6}>
            If the Company becomes aware that personal information of a Minor has been collected inadvertently, the
            Company will take reasonable steps to delete such information in accordance with applicable laws.
          </Text>

          <Title order={3} mt="xl">
            SECURITY MEASURES
          </Title>

          <Text lh={1.6}>
            The Company implements reasonable and appropriate technical and organisational measures to protect
            information processed through the Platform against unauthorised access, disclosure, alteration, loss, or
            destruction.
          </Text>

          <Text lh={1.6}>
            Such measures may include access controls, authentication mechanisms, encryption where appropriate, secure
            hosting environments, internal policies and procedures, and restricted access to information on a
            need-to-know basis. The Company also maintains logs and audit trails to monitor platform activity and
            document exchanges.
          </Text>

          <Text lh={1.6}>
            While the Company takes reasonable steps to safeguard information, no method of transmission over the
            internet or method of electronic storage is completely secure. Accordingly, the Company does not guarantee
            absolute security and Users acknowledge and accept this inherent risk.
          </Text>

          <Title order={3} mt="xl">
            COOKIES & TECHNICAL TRACKING
          </Title>

          <Text lh={1.6}>
            The Platform may use cookies and similar technologies to support functionality, enhance user experience,
            maintain security, and analyse platform usage. Cookies are small data files placed on a User's device that
            enable the Platform to recognise returning Users and remember certain preferences.
          </Text>

          <Text lh={1.6}>
            The Company may collect technical and usage information such as IP address, browser type, device
            information, access times, pages viewed, and interactions with the Platform. Such information is used for
            platform administration, security monitoring, analytics, troubleshooting, and service improvement.
          </Text>

          <Text lh={1.6}>
            Users may manage or disable cookies through their browser or device settings. Please note that disabling
            certain cookies may affect the availability or functionality of the Platform.
          </Text>

          <Text lh={1.6}>
            The Company does not use cookies or technical tracking technologies to collect sensitive personal
            information without consent, where such consent is required under applicable laws.
          </Text>

          <Title order={3} mt="xl">
            THIRD-PARTY LINKS & INTEGRATION
          </Title>

          <Text lh={1.6}>
            The Platform may contain links to third-party websites, platforms, services, or resources, or may integrate
            with third-party tools or services for functionality such as verification, payments, analytics, or
            communication.
          </Text>

          <Text lh={1.6}>
            The Company does not control and is not responsible for the privacy practices, content, or security of such
            third-party websites, platforms, or services. Any information shared by Users with third parties is governed
            by the privacy policies and terms of such third parties.
          </Text>

          <Text lh={1.6}>
            Users are encouraged to review the privacy policies of any third-party websites or services before accessing
            them or providing any information. The inclusion of a link or integration does not imply endorsement by the
            Company.
          </Text>

          <Title order={3} mt="xl">
            CHANGES TO THIS PRIVACY POLICY
          </Title>

          <Text lh={1.6}>
            The Company may update or modify this Privacy Policy from time to time to reflect changes in legal
            requirements, business practices, or the operation of the Platform.
          </Text>

          <Text lh={1.6}>
            Any updates to this Privacy Policy will be effective upon posting the revised version on the Platform,
            unless otherwise stated. The date of the latest update may be indicated at the beginning or end of this
            Privacy Policy.
          </Text>

          <Text lh={1.6}>
            Users are encouraged to review this Privacy Policy periodically to remain informed about how information is
            collected, used, and protected. Continued access to or use of the Platform after the effective date of any
            changes constitutes acceptance of the updated Privacy Policy.
          </Text>

          <Title order={3} mt="xl">
            GRIEVANCE REDRESSAL & CONTACT INFORMATION
          </Title>

          <Text lh={1.6}>
            If Users have any questions, concerns, requests, or grievances regarding this Privacy Policy or the
            processing of information through the Platform, they may contact the Company using the details set out
            below:
          </Text>

          <List type="ordered" spacing="sm" withPadding style={{ listStyleType: "lower-roman" }}>
            <List.Item>
              <Text lh={1.6}>
                <strong>Company Name:</strong>{" "}
                <strong>
                  <em>
                    <u>GT Setu Connections Pvt Ltd</u>
                  </em>
                </strong>
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                <strong>Registered Address:</strong>{" "}
                <strong>
                  <em>
                    <u>1011/19, Indira, Mitranagar Colony, Deep Bungalow Chowk, Pune 411016</u>
                  </em>
                </strong>
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                <strong>Contact Email:</strong>{" "}
                <strong>
                  <em>
                    <u>info@gtsetu.com</u>
                  </em>
                </strong>
              </Text>
            </List.Item>
            <List.Item>
              <Text lh={1.6}>
                <strong>Contact Number:</strong>{" "}
                <strong>
                  <em>
                    <u>+919175600107</u>
                  </em>
                </strong>
              </Text>
            </List.Item>
          </List>

          <Text lh={1.6}>
            Users may also be able to exercise certain rights, submit requests, or raise grievances directly through
            their account by accessing the link-{" "}
            <Anchor href="/support" fw={700} fs="italic">
              support
            </Anchor>{" "}
            made available on the Platform after logging in. The Company will make reasonable efforts to acknowledge and
            address grievances or requests in a timely manner and in accordance with applicable data protection and
            privacy laws. Where required under applicable law, the Company shall designate a grievance or data
            protection contact and update the relevant contact details on the Platform.
          </Text>

          <Text lh={1.6}>
            This Privacy Policy shall be read in conjunction with the Company's Terms and Conditions and other
            applicable policies, and together they govern the use of the Platform.
          </Text>

          <Text lh={1.6}>
            If any provision of this Privacy Policy is held to be invalid, unlawful, or unenforceable under applicable
            law, such provision shall be severed to the extent necessary, and the remaining provisions shall continue to
            remain in full force and effect. The failure of the Company to enforce any provision of this Privacy Policy
            shall not be deemed a waiver of such provision or any other provision.
          </Text>

          <Text lh={1.6}>
            This Privacy Policy applies for the duration of a User's interaction with the Platform and, to the extent
            necessary, shall survive termination of access or services in accordance with its terms.
          </Text>
        </Box>
      </Container>
    </Box>
  );
}
